/**
 * Create-page modal — POSTs to /api/v1/pages.
 *
 * Used in two flows:
 *  - "New page" from the space (no parent)
 *  - "Add sub-page" from an existing page (parent_id pre-set)
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "@/lib/api";
import type { Page } from "@/lib/types";

type Props = {
  open: boolean;
  onClose: () => void;
  spaceId: string | null;
  spaceSlug: string | null;
  parentId?: string | null;
  parentTitle?: string | null;
};

const STARTERS: { label: string; body: string }[] = [
  {
    label: "Blank",
    body: "",
  },
  {
    label: "Meeting notes",
    body: "## Attendees\n- \n\n## Agenda\n- \n\n## Decisions\n- \n\n## Action items\n- [ ] \n",
  },
  {
    label: "RFC",
    body: "## Context\n\n## Proposal\n\n## Alternatives considered\n\n## Tradeoffs\n\n## Decision\n",
  },
  {
    label: "Runbook",
    body: "## When to use this\n\n## Steps\n1. \n\n## Rollback\n\n## Owner\n",
  },
];

export function CreatePageModal({
  open,
  onClose,
  spaceId,
  spaceSlug,
  parentId = null,
  parentTitle = null,
}: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [starter, setStarter] = useState("Blank");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setTitle("");
    setBody("");
    setStarter("Blank");
  }, [open]);

  const submit = useMutation({
    mutationFn: async () => {
      if (!spaceId) throw new Error("No space selected.");
      const body_md = body || STARTERS.find((s) => s.label === starter)?.body || "";
      const payload = {
        space_id: spaceId,
        parent_id: parentId,
        title: title.trim(),
        body_md,
      };
      return (await api.post<Page>("/pages", payload)).data;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["pages", spaceSlug] });
      queryClient.invalidateQueries({ queryKey: ["page", created.id] });
      onClose();
      navigate(`/wiki/pages/${created.id}`);
    },
    onError: (err) => {
      const detail =
        (err as { response?: { data?: { detail?: unknown } } }).response?.data
          ?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? (detail[0] as { msg?: string })?.msg ?? "Could not create page."
            : "Could not create page.",
      );
    },
  });

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal
      aria-labelledby="create-page-title"
      onClick={onClose}
      style={overlay}
    >
      <div onClick={(e) => e.stopPropagation()} style={card}>
        <h2 id="create-page-title" style={{ fontSize: 20, fontWeight: 600, margin: "0 0 4px" }}>
          {parentId ? "New sub-page" : "New page"}
        </h2>
        {parentTitle && (
          <p style={{ fontSize: 12, color: "var(--mute)", margin: "0 0 16px" }}>
            Under <strong style={{ color: "var(--body)" }}>{parentTitle}</strong>
          </p>
        )}
        {!parentTitle && <div style={{ height: 12 }} />}

        <label style={fieldRow}>
          <span style={fieldLabel}>Title</span>
          <input
            autoFocus
            type="text"
            placeholder="Page title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={input}
            maxLength={512}
          />
        </label>

        <label style={fieldRow}>
          <span style={fieldLabel}>Starter template</span>
          <select
            value={starter}
            onChange={(e) => {
              setStarter(e.target.value);
              const tpl = STARTERS.find((s) => s.label === e.target.value);
              if (tpl) setBody(tpl.body);
            }}
            style={input}
          >
            {STARTERS.map((s) => (
              <option key={s.label} value={s.label}>
                {s.label}
              </option>
            ))}
          </select>
        </label>

        <label style={fieldRow}>
          <span style={fieldLabel}>Body (Markdown)</span>
          <textarea
            placeholder="Start writing… you can edit the page later."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            style={{
              ...input,
              resize: "vertical",
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          />
        </label>

        {error && (
          <p
            role="alert"
            style={{
              padding: "8px 12px",
              fontSize: 13,
              color: "#ee1d36",
              background: "rgba(238,29,54,0.06)",
              border: "1px solid rgba(238,29,54,0.3)",
              borderRadius: 4,
              marginTop: 12,
            }}
          >
            {error}
          </p>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button type="button" onClick={onClose} style={btnSecondary}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              if (!spaceId) {
                setError("Pick a space first.");
                return;
              }
              if (!title.trim()) {
                setError("Title is required.");
                return;
              }
              submit.mutate();
            }}
            disabled={submit.isPending}
            style={{
              ...btnPrimary,
              cursor: submit.isPending ? "wait" : "pointer",
              opacity: submit.isPending ? 0.6 : 1,
            }}
          >
            {submit.isPending ? "Creating…" : "Create page"}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  backdropFilter: "blur(4px)",
  display: "grid",
  placeItems: "center",
  zIndex: 100,
};

const card: CSSProperties = {
  width: "min(620px, calc(100vw - 32px))",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 24,
  boxShadow:
    "0 30px 18px rgba(0,0,0,0.06), 0 13px 13px rgba(0,0,0,0.10), 0 3px 7px rgba(0,0,0,0.12)",
  maxHeight: "calc(100vh - 64px)",
  overflowY: "auto",
};

const fieldRow: CSSProperties = { display: "block", marginBottom: 12 };

const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 4,
};

const input: CSSProperties = {
  width: "100%",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 10px",
  fontFamily: "var(--font-sans)",
  fontSize: 14,
  color: "var(--ink)",
  outline: "none",
};

const btnPrimary: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 13,
  fontWeight: 500,
};

const btnSecondary: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 13,
  color: "var(--ink)",
  cursor: "pointer",
};
